import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Card from '../../../../components/card/card.component';
import Button from '../../../../components/button/button.component';
import ModalButtonBlock from '../../../../components/modal-button-block/modal-button-block.component';
import ErrorBlock from '../../../../components/error-block/error-block.component';
import ModalLayout from '../../../../components/modal-layout/modal-layout.component';
import Loader from '../../../../components/loader/loader.component';
import ModalLabel from '../../../../components/modal-label/modal-label.component';
import ModalDescription from '../../../../components/modal-description/modal-description.component';
import ModalFieldBlock from '../../../../components/modal-field-block/modal-field-block.component';
import FieldSelect from '../../../../components/field-select/field-select.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import ScreenService from '../../../../services/screen-service';
import StructureService from '../../../../services/structure-service';
import useCurrentUserPermissions from '../../../../hooks/use-current-user-permissions';
import { exportData, resetExportFile } from './export-modal.action';

const ExportModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const EXPORT_FORMAT_INDEX_DEFAULT: number = 0;
  const EXPORT_RECONNECT_ATTEMPT_LIMIT: number = 3;
  const EXPORT_RECONNECT_INTERVAL: number = 5000;

  const {
    isOpen,
    withExportAll,
    exportTab,
    defaultExportFormat,
    defaultExportType,
    exportEntityType,
    entityIds,
    portfolioIds,
    portfolioGroupIds,
    status,
    dealId,
    exportFormatList,
    exportTypeList,
    onSuccess,
    onCloseModal,
  } = props;

  const dispatch: ReduxDispatch = useDispatch();
  const permissionList: string[] = useCurrentUserPermissions();
  const exportFile: FileItem | null = useSelector(({ exportModal }: Reducers): FileItem | null => (
    exportModal.exportFile
  ));
  const isLoading: boolean = useSelector(({ exportModal }: Reducers): boolean => exportModal.isLoading);

  let exportReconnectIndex: number = 0;

  const [exportFormat, setExportFormat]: [string | null, Function] = useState(null);
  const [exportFormatDescription, setExportFormatDescription]: [string | null, Function] = useState(null);
  const [exportType, setExportType]: [string | null, Function] = useState(null);
  const [errorMessage, setErrorMessage]: [string | null, Function] = useState(null);
  const [exportedTab, setExportedTab]: [string | null, Function] = useState(null);

  const menuItemExportFormatList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: 'label',
    optionList: exportFormatList,
    selectedValue: exportFormat,
  }), [exportFormatList, exportFormat]);

  const menuItemExportTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: 'label',
    optionList: exportTypeList,
    selectedValue: exportType,
  }), [exportTypeList, exportType]);

  const exportFormatChange: Function = useCallback((exportFormatName: string): void => {
    const exportFormatValue: FormatItem = exportFormatList.find((exportFormatItem: FormatItem): boolean => (
      exportFormatItem.name === exportFormatName
    ));
    setExportFormat(exportFormatName);
    setExportFormatDescription((exportFormatValue && exportFormatValue.description) || null);
  }, [exportFormatList]);

  const exportTypeChange: Function = useCallback((exportTypeName: string): void => {
    setExportType(exportTypeName);
  }, []);

  const closeModal: Function = useCallback((): void => {
    if (onCloseModal) {
      onCloseModal();
    }

    setErrorMessage(null);
  }, []);

  const sendForm: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): Promise<ActionCallback> | null => {
    if (!exportEntityType || !exportFormat) {
      return null;
    }

    if ((!entityIds || !entityIds.length) && !dealId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(exportData({
        exportForm: {
          entityIds,
          entityType: exportEntityType,
          exportFormat,
          exportType,
          exportTab,
          withExportAll,
          portfolioIds,
          portfolioGroupIds,
          status,
          dealId,
        },
      }, (response: ActionResponse): void => {
        closeModal();

        setErrorMessage(null);
        setExportedTab(exportTab || null);

        if (onSuccess) {
          onSuccess();
        }

        if (exportReconnectIndex) {
          exportReconnectIndex = 0;
        }

        resolve(response);
      }, (exception: ServerError, originalError: ServerError): void => {
        if (exception && exception.message) {
          setErrorMessage(exception.message);
        }

        if (!exception && originalError) {
          if (exportReconnectIndex <= EXPORT_RECONNECT_ATTEMPT_LIMIT) {
            setTimeout((): void => {
              sendForm(event);

              exportReconnectIndex++;
            }, EXPORT_RECONNECT_INTERVAL);
          } else {
            setErrorMessage('Cannot generate the export file. Probably file is too large to process '
              + ' or some error occurred during the data processing.');
          }
        } else {
          exportReconnectIndex = 0;
        }
      }));
    });
  }, [exportEntityType, exportFormat, entityIds, dealId, exportType, exportTab, withExportAll, exportReconnectIndex]);

  useEffect((): void => {
    let currentExportType: string | null = exportType;

    if (exportTypeList) {
      exportTypeList.forEach((exportTypeItem: ExportTypeItem, exportTypeIndex: number): void => {
        if (exportTypeItem.permission && !StructureService.checkPermission(exportTypeItem.permission, permissionList)) {
          delete exportTypeList[exportTypeIndex];
        }

        if (!currentExportType
          && ((defaultExportType && defaultExportType === exportTypeItem.name) || !defaultExportType)
          && ((exportTypeItem.permission && StructureService.checkPermission(exportTypeItem.permission, permissionList))
            || !exportTypeItem.permission)) {
          setExportType(exportTypeItem.name);
          currentExportType = exportTypeItem.name;
        }
      });
    }
  }, [exportTypeList]);

  useEffect((): void => {
    if (!exportFormat && exportFormatList) {
      exportFormatList.forEach((exportFormatItem: ExportTypeItem, exportFormatIndex: number): void => {
        if ((defaultExportFormat && defaultExportFormat === exportFormatItem.name)
          || (!defaultExportFormat && exportFormatIndex === EXPORT_FORMAT_INDEX_DEFAULT)) {
          setExportFormat(exportFormatItem.name);
          setExportFormatDescription(exportFormatItem.description || null);
        }
      });
    }
  }, [exportFormatList]);

  useEffect((): void => {
    if (exportFile && exportFile.link && (!exportTab || (exportTab && exportedTab === exportTab))) {
      ScreenService.downloadFile(exportFile.link);
      dispatch(resetExportFile());
      setExportedTab(null);
    }
  }, [exportFile, exportedTab]);

  return (
    <Fragment>
      {
        (!!entityIds || !!dealId) && !!exportEntityType && (
          <ModalLayout isOpen={isOpen} title="Export Data" onCloseModal={closeModal}>
            <Card isContainer>
              <ModalLabel>
                Select format of export:
              </ModalLabel>
              <ModalFieldBlock>
                <FieldSelect
                  isPlain
                  isBordered
                  value={exportFormat}
                  onChange={(event: EventSelectChange, value: string): void => {
                    exportFormatChange(value);
                  }}
                >
                  {menuItemExportFormatList}
                </FieldSelect>
              </ModalFieldBlock>
              {
                exportFormatDescription && (
                  <ModalDescription>
                    {exportFormatDescription}
                  </ModalDescription>
                )
              }
              {
                exportTypeList && (
                  <Fragment>
                    <ModalLabel>
                      Select type of export:
                    </ModalLabel>
                    <ModalFieldBlock>
                      <FieldSelect
                        isPlain
                        isBordered
                        value={exportType}
                        onChange={(event: EventSelectChange, value: string): void => {
                          exportTypeChange(value);
                        }}
                      >
                        {menuItemExportTypeList}
                      </FieldSelect>
                    </ModalFieldBlock>
                  </Fragment>
                )
              }
            </Card>
            <ModalButtonBlock>
              <Button isActive onClick={sendForm}>
                Export
              </Button>
              {
                isLoading && (
                  <Loader isInline isModal isSmall />
                )
              }
            </ModalButtonBlock>
            {
              !!errorMessage && (
                <ErrorBlock errorMessage={errorMessage} />
              )
            }
          </ModalLayout>
        )
      }
    </Fragment>
  );
});

export default ExportModal;
